import React, { useState, useEffect } from 'react';
import { Modal } from '../common/Modal/Modal';
import styles from './RelatiebeheerModule.module.css';

interface ContactModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (contact: any) => void;
  onDelete?: () => void;
  contact?: any;
  projectId: string;
}

export const ContactModal: React.FC<ContactModalProps> = ({ isOpen, onClose, onSave, onDelete, contact, projectId }) => {
  const [formData, setFormData] = useState({
    name: '',
    role: '',
    organization: '',
    email: '',
    phone: '',
    address: '',
    influenceLevel: 'medium' as 'high' | 'medium' | 'low' | 'very_high',
    supportLevel: 'neutral' as 'supporter' | 'neutral' | 'opponent',
    tags: [] as string[],
    notes: '',
    category: 'stakeholder',
    preferredContact: 'email'
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (contact) {
      setFormData({
        name: contact.name || '',
        role: contact.role || '',
        organization: contact.organization?.name || '',
        email: contact.contact?.email || '',
        phone: contact.contact?.phone || '',
        address: contact.contact?.address?.street || '',
        influenceLevel: contact.influence?.level || 'medium',
        supportLevel: contact.role === 'supporter' ? 'supporter' :
                     contact.role === 'opponent' ? 'opponent' : 'neutral',
        tags: contact.tags || [],
        notes: contact.notes || '',
        category: contact.category || 'stakeholder',
        preferredContact: contact.contact?.preferredMethod || 'email'
      });
    }
  }, [contact]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Naam is verplicht';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'E-mail is verplicht';
    }
    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Ongeldig e-mailadres';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (validate()) {
      // Format data for backend API
      const contactData = {
        project: projectId,
        type: formData.organization ? 'organization' : 'individual',
        name: formData.name,
        organization: formData.organization ? {
          name: formData.organization,
          type: 'other'
        } : undefined,
        contact: {
          email: formData.email,
          phone: formData.phone,
          address: formData.address ? {
            street: formData.address
          } : undefined,
          preferredMethod: formData.preferredContact
        },
        role: formData.supportLevel === 'supporter' ? 'supporter' :
              formData.supportLevel === 'opponent' ? 'opponent' : 'neutral',
        category: formData.category,
        influence: {
          level: formData.influenceLevel
        },
        sentiment: {
          current: formData.supportLevel === 'supporter' ? 'positive' :
                  formData.supportLevel === 'opponent' ? 'negative' : 'neutral'
        },
        tags: formData.tags,
        notes: formData.notes
      };

      onSave(contactData);
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={contact ? 'Contact bewerken' : 'Nieuw contact toevoegen'}
    >
      <form onSubmit={handleSubmit} className={styles.modalForm}>
        {/* Algemene informatie */}
        <div className={styles.formSection}>
          <h3 className={styles.sectionTitle}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
            Algemene informatie
          </h3>
          
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label className={styles.label}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ opacity: 0.7 }}>
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
                Naam
                <span className={styles.required}>*</span>
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className={`${styles.input} ${errors.name ? styles.error : ''}`}
                placeholder="Volledige naam"
              />
              {errors.name && <span className={styles.errorText}>{errors.name}</span>}
            </div>
            
            <div className={styles.formGroup}>
              <label className={styles.label}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ opacity: 0.7 }}>
                  <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="8.5" cy="7" r="4" />
                  <line x1="21" y1="11" x2="17" y2="11" />
                  <line x1="21" y1="14" x2="17" y2="14" />
                  <line x1="21" y1="17" x2="17" y2="17" />
                </svg>
                Functie/Rol
              </label>
              <input
                type="text"
                name="role"
                value={formData.role}
                onChange={handleChange}
                className={styles.input}
                placeholder="bijv. Projectmanager, Bewoner"
              />
            </div>
          </div>
          
          <div className={styles.formGroup}>
            <label className={styles.label}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ opacity: 0.7 }}>
                <path d="M3 21h18" />
                <path d="M5 21V7l8-4v18" />
                <path d="M19 21V11l-6-4" />
                <rect x="9" y="9" width="4" height="4" />
                <rect x="9" y="14" width="4" height="4" />
                <rect x="14" y="14" width="4" height="4" />
              </svg>
              Organisatie
            </label>
            <input
              type="text"
              name="organization"
              value={formData.organization}
              onChange={handleChange}
              className={styles.input}
              placeholder="Naam van de organisatie"
            />
          </div>
        </div>
        
        {/* Contactgegevens */}
        <div className={styles.formSection}>
          <h3 className={styles.sectionTitle}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
              <polyline points="22,6 12,13 2,6" />
            </svg>
            Contactgegevens
          </h3>
          
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label className={styles.label}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ opacity: 0.7 }}>
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                  <polyline points="22,6 12,13 2,6" />
                </svg>
                E-mailadres
                <span className={styles.required}>*</span>
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={`${styles.input} ${errors.email ? styles.error : ''}`}
                placeholder="email@example.nl"
              />
              {errors.email && <span className={styles.errorText}>{errors.email}</span>}
            </div>
            
            <div className={styles.formGroup}>
              <label className={styles.label}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ opacity: 0.7 }}>
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                </svg>
                Telefoonnummer
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className={styles.input}
                placeholder="06-12345678"
              />
            </div>
          </div>
          
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label className={styles.label}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ opacity: 0.7 }}>
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                  <circle cx="12" cy="10" r="3" />
                </svg>
                Adres
              </label>
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleChange}
                className={styles.input}
                placeholder="Straat en huisnummer"
              />
            </div>
            
            <div className={styles.formGroup}>
              <label className={styles.label}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ opacity: 0.7 }}>
                  <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
                </svg>
                Voorkeur contact
              </label>
              <select
                name="preferredContact"
                value={formData.preferredContact}
                onChange={handleChange}
                className={styles.select}
              >
                <option value="email">E-mail</option>
                <option value="phone">Telefoon</option>
                <option value="letter">Brief</option>
                <option value="in_person">Persoonlijk</option>
              </select>
            </div>
          </div>
        </div>
        
        {/* Invloed & Betrokkenheid */}
        <div className={styles.formSection}>
          <h3 className={styles.sectionTitle}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
            Invloed & Betrokkenheid
          </h3>
          
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label className={styles.label}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ opacity: 0.7 }}>
                  <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
                </svg>
                Invloedniveau
              </label>
              <select
                name="influenceLevel"
                value={formData.influenceLevel}
                onChange={handleChange}
                className={styles.select}
              >
                <option value="very_high">Zeer hoog</option>
                <option value="high">Hoog</option>
                <option value="medium">Gemiddeld</option>
                <option value="low">Laag</option>
              </select>
            </div>
            
            <div className={styles.formGroup}>
              <label className={styles.label}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ opacity: 0.7 }}>
                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                </svg>
                Houding t.o.v. project
              </label>
              <select
                name="supportLevel"
                value={formData.supportLevel}
                onChange={handleChange}
                className={styles.select}
              >
                <option value="supporter">Voorstander</option>
                <option value="neutral">Neutraal</option>
                <option value="opponent">Tegenstander</option>
              </select>
            </div>
          </div>
          
          <div className={styles.formGroup}>
            <label className={styles.label}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ opacity: 0.7 }}>
                <path d="M3 3h18v18H3zM21 9H3M21 15H3M12 3v18" />
              </svg>
              Categorie
            </label>
            <select
              name="category"
              value={formData.category}
              onChange={handleChange}
              className={styles.select}
            >
              <option value="stakeholder">Stakeholder</option>
              <option value="authority">Bevoegd gezag</option>
              <option value="resident">Omwonende</option>
              <option value="business">Bedrijf</option>
              <option value="ngo">NGO/Belangengroep</option>
              <option value="media">Media</option>
              <option value="expert">Expert/Adviseur</option>
              <option value="other">Overig</option>
            </select>
          </div>
        </div>
        
        {/* Extra informatie */}
        <div className={styles.formSection}>
          <h3 className={styles.sectionTitle}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="16" y1="13" x2="8" y2="13" />
              <line x1="16" y1="17" x2="8" y2="17" />
              <polyline points="10 9 9 9 8 9" />
            </svg>
            Extra informatie
          </h3>
          
          <div className={styles.formGroup}>
            <label className={styles.label}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ opacity: 0.7 }}>
                <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
                <line x1="7" y1="7" x2="7.01" y2="7" />
              </svg>
              Tags
              <span className={styles.helpText}>(gescheiden door komma's)</span>
            </label>
            <input
              type="text"
              value={formData.tags.join(', ')}
              onChange={(e) => setFormData({
                ...formData,
                tags: e.target.value.split(',').map(t => t.trim()).filter(t => t)
              })}
              className={styles.input}
              placeholder="bijv. Beslisser, Invloedrijk, Kritisch"
            />
          </div>
          
          <div className={styles.formGroup}>
            <label className={styles.label}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ opacity: 0.7 }}>
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="16" y1="13" x2="8" y2="13" />
                <line x1="16" y1="17" x2="8" y2="17" />
                <polyline points="10 9 9 9 8 9" />
              </svg>
              Notities
            </label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              className={styles.textarea}
              rows={4}
              placeholder="Extra opmerkingen of aandachtspunten..."
            />
          </div>
        </div>
        
        {/* Action buttons */}
        <div className={styles.modalActions}>
          {contact && onDelete && (
            <button
              type="button"
              onClick={() => {
                if (window.confirm('Weet u zeker dat u dit contact wilt verwijderen?')) {
                  onDelete();
                  onClose();
                }
              }}
              className={styles.btnDanger}
              style={{ marginRight: 'auto' }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
              </svg>
              Verwijderen
            </button>
          )}
          <button type="button" onClick={onClose} className={styles.btnSecondary}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
            Annuleren
          </button>
          <button type="submit" className={styles.btnPrimary}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
              <polyline points="17 21 17 13 7 13 7 21" />
              <polyline points="7 3 7 8 15 8" />
            </svg>
            {contact ? 'Wijzigingen opslaan' : 'Contact toevoegen'}
          </button>
        </div>
      </form>
    </Modal>
  );
};